<template>
  <div v-if="callerId" class="sp-dialer sp-incoming-global">
    <div class="sp-dialer__incoming" role="alertdialog" aria-label="Incoming call">
      <div class="sp-dialer__incoming-pulse" aria-hidden="true">
        <v-icon icon="mdi-phone-ring" size="26" />
      </div>
      <div class="sp-dialer__incoming-text">
        <span class="sp-dialer__incoming-label">Incoming call</span>
        <span class="sp-dialer__incoming-number">{{ callerId }}</span>
      </div>
      <div class="sp-dialer__incoming-actions">
        <button
          type="button"
          class="sp-dialer__incoming-decline"
          aria-label="Decline call"
          @click="decline"
        >
          <v-icon icon="mdi-phone-hangup" size="20" />
        </button>
        <button
          type="button"
          class="sp-dialer__incoming-accept"
          aria-label="Answer call"
          @click="accept"
        >
          <v-icon icon="mdi-phone" size="20" />
        </button>
      </div>
    </div>
  </div>

  <!--
    Once answered from the global popup above, the call needs controls
    regardless of which page the agent is on -- SoftphoneDialer's own
    in-call view only exists on Campaign Detail. Suppressed there via
    suppressInCallBar so the two don't both render for the same call.
  -->
  <div
    v-else-if="inCall && !suppressInCallBar"
    class="sp-dialer sp-incall-global"
  >
    <div class="sp-dialer__incall-bar" role="group" aria-label="Active call">
      <v-icon :icon="muted ? 'mdi-microphone-off' : 'mdi-phone-in-talk'" size="20" />
      <span class="sp-dialer__incall-number">{{ activeCallerId || 'Unknown number' }}</span>
      <span class="sp-dialer__incall-timer">
        {{ phase === 'ringing' ? 'Ringing…' : formatDuration(durationSec) }}
      </span>
      <button
        type="button"
        class="sp-dialer__incall-btn"
        :class="{ 'is-active': muted }"
        :aria-pressed="muted"
        :aria-label="muted ? 'Unmute microphone' : 'Mute microphone'"
        @click="toggleMute"
      >
        <v-icon :icon="muted ? 'mdi-microphone-off' : 'mdi-microphone'" size="18" />
      </button>
      <button
        type="button"
        class="sp-dialer__incall-btn sp-dialer__incall-btn--danger"
        aria-label="End call"
        @click="endCall"
      >
        <v-icon icon="mdi-phone-hangup" size="18" />
      </button>
    </div>
  </div>
</template>

<script setup>
// Mounted once, app-wide (LandingPage.vue shell) -- unlike SoftphoneDialer,
// which is only rendered on the Campaign Detail page for outbound dialing.
// A genuinely inbound call (e.g. an on-call redirect to this agent) can
// arrive while the agent is on ANY page, so both the ring/answer popup and
// the post-answer call controls need to work from anywhere, not just
// Campaign Detail.
import { onBeforeUnmount, onMounted, ref } from 'vue'
import {
  answerIncomingCall,
  declineIncomingCall,
  getSoftphoneStatus,
  hangupSoftphoneCall,
  setSoftphoneMuted,
} from '@/services/softphone'
import '@/styles/softphone/dialer.css'

defineProps({
  suppressInCallBar: { type: Boolean, default: false },
})

const callerId = ref(null)
const activeCallerId = ref(null)
const inCall = ref(false)
const muted = ref(false)
const phase = ref('idle')
const durationSec = ref(0)
let pollTimer = null

function formatDuration(totalSec) {
  const sec = Math.max(0, Number(totalSec) || 0)
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function refresh() {
  const status = getSoftphoneStatus()
  callerId.value = status.incomingCall?.callerId ?? null
  inCall.value = status.inCall
  muted.value = Boolean(status.muted)
  phase.value = status.phase || (status.inCall ? 'connected' : 'idle')
  durationSec.value = status.durationSec || 0
  if (!status.inCall) activeCallerId.value = null
}

function accept() {
  // Capture the number before answering clears the ringing state -- the
  // in-call bar needs it after the popup itself disappears.
  activeCallerId.value = callerId.value
  answerIncomingCall()
  refresh()
}

function decline() {
  declineIncomingCall()
  refresh()
}

function toggleMute() {
  const next = !muted.value
  const ok = setSoftphoneMuted(next)
  if (ok) muted.value = next
  refresh()
}

function endCall() {
  hangupSoftphoneCall()
  refresh()
}

onMounted(() => {
  refresh()
  pollTimer = window.setInterval(refresh, 400)
})

onBeforeUnmount(() => {
  if (pollTimer) window.clearInterval(pollTimer)
})
</script>

<style scoped>
.sp-incoming-global,
.sp-incall-global {
  position: static;
}

.sp-dialer__incall-bar {
  position: fixed;
  right: 1.25rem;
  bottom: 6rem;
  z-index: 1300;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.9rem;
  border-radius: 999px;
  background: var(--sp-surface);
  box-shadow: var(--sp-panel-shadow);
  border: 1px solid var(--sp-line);
  color: var(--sp-ink);
}

.sp-dialer__incall-number {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sp-ink);
  white-space: nowrap;
}

.sp-dialer__incall-timer {
  font-size: 0.8rem;
  color: var(--sp-muted);
  font-variant-numeric: tabular-nums;
  min-width: 3rem;
}

.sp-dialer__incall-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.06);
  color: var(--sp-ink);
  cursor: pointer;
  transition: filter 0.15s ease, background 0.15s ease;
}

.sp-dialer__incall-btn.is-active {
  background: var(--sp-danger);
  color: #fff;
}

.sp-dialer__incall-btn--danger {
  background: var(--sp-danger);
  color: #fff;
}

.sp-dialer__incall-btn:hover {
  filter: brightness(1.08);
}
</style>
